import { t } from '@lingui/core/macro';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { ResearcherShell } from '@/components/navigation/researcher-shell';
import { getPublicEnv } from '@/utils/session/get-public-env';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

import { MessagesTab } from '../projects/$projectId/-details/messages-tab';

const messagesSearchSchema = z.object({
  /** Uid of the open conversation — in the URL so deep links and back
   * navigation land on the right thread. */
  conversation: z.string().optional(),
  /**
   * Legacy deep-link param — the legacy dashboard messages page's name for the
   * same thing, still emitted by notification emails and legacy links. An
   * alias for `conversation`.
   */
  conversationUid: z.string().optional(),
});

export const Route = createFileRoute('/researchers/_authenticated/messages/')({
  validateSearch: messagesSearchSchema,
  beforeLoad: async ({ context, location, search }) => {
    const resolved = await resolveActiveDashboard({ context, location });

    // With the new UI off there is no new-app messages surface, so fall back
    // to the legacy dashboard inbox entirely.
    if (!resolved.v3Enabled) {
      const organizationId = resolved.activeDashboard.organization ?? undefined;
      const teamId = resolved.activeDashboard.team ?? undefined;
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0 && organizationId && teamId) {
        // Preserve the deep-linked thread across the fallback. Notification
        // emails now point here with `?conversationUid=…`; the legacy inbox
        // selects the thread from that same param, so forward it (accepting
        // the `conversation` alias too) instead of dropping it.
        const conversationUid = search.conversationUid ?? search.conversation;
        const legacyBase = `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/messages`;
        throw redirect({
          href: conversationUid
            ? `${legacyBase}?conversationUid=${encodeURIComponent(conversationUid)}`
            : legacyBase,
        });
      }

      // No legacy target available (missing website URL / org / team) — never
      // expose the new inbox behind a disabled flag.
      throw redirect({ to: '/researchers/projects' });
    }

    return resolved;
  },
  component: MessagesPage,
  head: () => ({ meta: [{ title: t`Messages` }] }),
});

/**
 * The top-level researcher Messages page: the team-wide inbox across every
 * project on the active team. Reuses the project details Messages tab UI in
 * its team scope — conversation list on the left, the open thread with a
 * composer on the right. New conversations start from a project's own
 * Messages tab (or its Participants tab), so there is no compose here.
 */
function MessagesPage() {
  const { user, activeDashboard, env } = Route.useRouteContext();
  const teamId = activeDashboard.team ?? undefined;
  const { conversation, conversationUid } = Route.useSearch();
  const navigate = Route.useNavigate();

  const selectConversation = (selected: string | null) => {
    void navigate({
      search: { conversation: selected ?? undefined },
    });
  };

  return (
    <div className="flex h-screen flex-col print:block print:h-auto">
      <ResearcherShell
        user={user}
        activeDashboard={activeDashboard}
        mainClassName="overflow-clip"
      >
        <div className="flex h-full min-h-0 flex-col px-6 py-8">
          <div className="mx-auto flex min-h-0 w-full max-w-360 flex-1 flex-col">
            <h1 className="font-heading text-display-xs font-semibold text-primary">
              {t`Messages`}
            </h1>

            <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
              <MessagesTab
                teamId={teamId}
                userId={user.id}
                enabled
                pusherAppKey={env.PUSHER_APP_KEY}
                // The legacy `conversationUid` param is an alias for
                // `conversation`.
                selectedConversationUid={
                  conversation ?? conversationUid ?? null
                }
                onSelectConversation={selectConversation}
              />
            </div>
          </div>
        </div>
      </ResearcherShell>
    </div>
  );
}
